import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { Heart, ShoppingCart } from 'lucide-react'
import type { ApiError } from '@/lib/api';
import { api } from '@/lib/api'
import { formatPrice, localizedName } from '@/lib/format'
import { haptic } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { useStoreTheme } from '@/lib/useStoreTheme'
import type { WishlistItem } from '@/shared/types'

import { StorefrontPage, StorefrontCard, StorefrontEmptyState, StorefrontLoading } from '@/components/StorefrontPage'

export function WishlistPage() {
  const lang = useAppStore((s) => s.lang)
  const setCartCount = useAppStore((s) => s.setCartCount)
  const T = useT('wishlist')
  const common = useT('common')
  const cartT = useT('cart')
  const qc = useQueryClient()
  const navigate = useNavigate()
  const theme = useStoreTheme()

  const { data: items, isLoading, error: loadError } = useQuery<WishlistItem[]>({
    queryKey: ['wishlist'],
    queryFn: () => api.get<WishlistItem[]>('/wishlist'),
  })

  const toggleMutation = useMutation<{ added: boolean }, ApiError, number>({
    mutationFn: (productId) => api.post<{ added: boolean }>(`/wishlist/${productId}`, {}),
    onSuccess: async () => {
      haptic('light')
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['wishlist'] }),
        qc.invalidateQueries({ queryKey: ['products'] }),
      ])
    },
    onError: () => {
      haptic('error')
    },
  })

  const clearMutation = useMutation<{ removed_count: number }, ApiError>({
    mutationFn: () => api.delete<{ removed_count: number }>('/wishlist'),
    onSuccess: async () => {
      haptic('medium')
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['wishlist'] }),
        qc.invalidateQueries({ queryKey: ['products'] }),
      ])
    },
    onError: () => {
      haptic('error')
    },
  })

  const addMutation = useMutation<{ cart_item_id: number }, ApiError, number>({
    mutationFn: (productId) =>
      api.post<{ cart_item_id: number }>('/cart/add', { product_id: productId, qty: 1 }),
    onSuccess: async () => {
      haptic('success')
      qc.invalidateQueries({ queryKey: ['cart'] })
      try {
        const cart = await api.get<{ count: number }>('/cart')
        setCartCount(cart.count)
      } catch {
        // Query invalidation handles cart refresh in background; do not fail successful add
      }
    },
    onError: () => {
      haptic('error')
      alert(T.addFail)
    },
  })

  if (loadError) return <p role="alert" className="p-4 text-red-600">{common.error}</p>
  if (isLoading) return <StorefrontLoading message={T.loading} />

  const wishlist = items ?? []

  if (wishlist.length === 0) {
    return (
      <StorefrontEmptyState
        icon={Heart}
        title={T.empty}
        action={{
          label: T.openCatalog,
          onClick: () => void navigate({ to: '/catalog' }),
        }}
      />
    )
  }

  return (
    <StorefrontPage
      title={T.title}
      badge={wishlist.length}
      action={
        <button
          onClick={() => clearMutation.mutate()}
          disabled={clearMutation.isPending || toggleMutation.isPending}
          className="text-xs font-semibold text-rose-500 hover:text-rose-400 active:scale-95 disabled:opacity-50 transition"
        >
          {T.clear}
        </button>
      }
    >
      {(toggleMutation.error || clearMutation.error) && (
        <p role="alert" className="mb-3 text-xs text-rose-500 font-medium">
          {common.actionFailed}
        </p>
      )}
      <ul className="space-y-3">
        {wishlist.map((item) => {
          const name = localizedName(lang, item.name_uk, item.name_en)
          const isAdding = addMutation.isPending && addMutation.variables === item.product_id
          const isTogglingThis = toggleMutation.isPending && toggleMutation.variables === item.product_id
          const isRemoving = isTogglingThis || clearMutation.isPending
          return (
            <li key={item.product_id}>
              <StorefrontCard className="overflow-hidden">
                <div className="flex gap-3.5 p-3.5">
                  {item.photo_url ? (
                    <Link
                      to="/catalog/$productSlug"
                      params={{ productSlug: item.product_slug || String(item.product_id) }}
                      className="shrink-0"
                    >
                      <img
                        src={item.photo_url}
                        alt={name}
                        className="h-20 w-20 rounded-xl object-cover"
                      />
                    </Link>
                  ) : (
                    <Link
                      to="/catalog/$productSlug"
                      params={{ productSlug: item.product_slug || String(item.product_id) }}
                      className={`flex h-20 w-20 shrink-0 items-center justify-center rounded-xl text-3xl ${
                        theme.isDarkTheme ? 'bg-[#1a202c]' : 'bg-slate-100'
                      }`}
                    >
                      📦
                    </Link>
                  )}
                  <div className="min-w-0 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          to="/catalog/$productSlug"
                          params={{ productSlug: item.product_slug || String(item.product_id) }}
                        >
                          <p className={`line-clamp-2 text-sm font-semibold leading-tight ${theme.textPrimaryClass}`}>
                            {name}
                          </p>
                        </Link>
                        <button
                          aria-label={`${cartT.remove}: ${name}`}
                          disabled={isRemoving || toggleMutation.isPending}
                          onClick={() => toggleMutation.mutate(item.product_id)}
                          className="shrink-0 text-rose-500 hover:text-rose-400 disabled:opacity-50 p-1 -m-1"
                        >
                          <Heart
                            className={`h-4.5 w-4.5 fill-current ${
                              isTogglingThis ? 'opacity-40 animate-pulse' : ''
                            }`}
                          />
                        </button>
                      </div>
                      <p className={`mt-1 text-sm font-bold ${theme.accentTextClass}`}>
                        {formatPrice(item.price_uah)}
                      </p>
                      {!item.in_stock && (
                        <p className="mt-0.5 text-[11px] text-slate-400 font-medium">
                          {T.outOfStock}
                        </p>
                      )}
                    </div>
                    <button
                      disabled={!item.in_stock || isAdding || isRemoving}
                      onClick={() => addMutation.mutate(item.product_id)}
                      className={`mt-2 flex items-center justify-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold shadow-2xs transition active:scale-95 ${
                        item.in_stock
                          ? theme.accentBtnClass
                          : theme.isDarkTheme
                            ? 'cursor-not-allowed bg-[#1a202c] text-slate-500'
                            : 'cursor-not-allowed bg-slate-100 text-slate-400'
                      }`}
                    >
                      {isAdding ? (
                        T.adding
                      ) : (
                        <>
                          <ShoppingCart className="h-3.5 w-3.5" /> {T.addToCart}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </StorefrontCard>
            </li>
          )
        })}
      </ul>
    </StorefrontPage>
  )
}
